/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { Button, Text } from "@vector-im/compound-web";
import {
  type FC,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "../Modal.tsx";
import {
  ProtocolistSessionInactiveError,
  type ProtocolistRecipientsMode,
  type SaveProtocolistRecipientsInput,
  type UseProtocolistRecordingResult,
} from "./index.ts";
import styles from "./ProtocolistRecipientsModal.module.css";

interface Props {
  open: boolean;
  onDismiss: () => void;
  protocolist: UseProtocolistRecordingResult;
}

export const ProtocolistRecipientsModal: FC<Props> = ({
  open,
  onDismiss,
  protocolist,
}) => {
  const { t } = useTranslation();
  const [selectedMode, setSelectedMode] =
    useState<ProtocolistRecipientsMode>("owner");
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<Error | null>(null);

  // Сессия закончилась, пока окно открыто: выбирать получателей уже не для чего.
  const sessionGone =
    saveError instanceof ProtocolistSessionInactiveError ||
    protocolist.recipientsError instanceof ProtocolistSessionInactiveError;

  // Человеку показываем свою фразу, а не текст ошибки из исходников: раньше в
  // окне висело английское «Protocolist session is not active».
  const errorText = ((): string | null => {
    if (sessionGone) {
      return t("protocolist.recipients.session_finished");
    }
    if (saveError) {
      return t("protocolist.recipients.save_error");
    }
    if (protocolist.recipientsError) {
      return t("protocolist.recipients.load_error");
    }
    return null;
  })();

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!protocolist.recipients && !protocolist.recipientsLoading) {
      void protocolist.loadRecipients().catch(() => {});
    }
  }, [open, protocolist]);

  useEffect(() => {
    if (!protocolist.recipients) {
      return;
    }

    setSelectedMode(protocolist.recipients.policy.mode);
    setSelectedUserIds(protocolist.recipients.policy.selected_user_ids);
  }, [protocolist.recipients]);

  const selectedSet = useMemo(() => new Set(selectedUserIds), [selectedUserIds]);

  const toggleUser = (userId: string): void => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return Array.from(next);
    });
  };

  const onSave = async (): Promise<void> => {
    let payload: SaveProtocolistRecipientsInput;
    switch (selectedMode) {
      case "owner":
      case "all_participants":
      case "self":
        payload = { mode: selectedMode };
        break;
      case "selected":
        payload = {
          mode: "selected",
          selected_user_ids: selectedUserIds,
        };
        break;
    }

    setSaving(true);
    setSaveError(null);
    try {
      await protocolist.saveRecipients(payload);
      onDismiss();
    } catch (err) {
      setSaveError(err instanceof Error ? err : new Error("save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={t("protocolist.recipients.title")}
      open={open}
      onDismiss={onDismiss}
    >
      <div className={styles.content}>
        {protocolist.recipientsLoading && (
          <Text>{t("common.loading")}</Text>
        )}
        {!protocolist.recipientsLoading && !sessionGone && protocolist.recipients && (
          <>
            <div className={styles.section}>
              {protocolist.recipients.options.map((option) => (
                <label key={option.mode} className={styles.optionRow}>
                  <input
                    type="radio"
                    name="protocolist-recipient-mode"
                    checked={selectedMode === option.mode}
                    onChange={() => setSelectedMode(option.mode)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
            {selectedMode === "selected" && (
              <div className={styles.section}>
                <Text>{t("protocolist.recipients.selected_title")}</Text>
                <div className={styles.candidateList}>
                  {protocolist.recipients.candidates.map((candidate) => (
                    <label key={candidate.user_id} className={styles.optionRow}>
                      <input
                        type="checkbox"
                        checked={selectedSet.has(candidate.user_id)}
                        onChange={() => toggleUser(candidate.user_id)}
                      />
                      <span>{candidate.display_name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
        {errorText && <Text>{errorText}</Text>}
        <div className={styles.actions}>
          <Button kind="secondary" onClick={onDismiss}>
            {t("common.back")}
          </Button>
          {!sessionGone && (
            <Button
              kind="primary"
              onClick={() => {
                void onSave();
              }}
              disabled={saving || protocolist.recipientsLoading}
            >
              {t("common.next")}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};
